<template>
  <StakeModal v-model="isOpen">
    <div class="tw-flex tw-flex-col tw-gap-5 tw-p-5">
      <h2 class="tw-text-lg tw-font-bold tw-text-gray-700 tw-mb-2">
        Connect this meeting to a person?
      </h2>

      <div class="tw-text-sm tw-text-gray-600">
        Link this meeting to a Prospect or Client so it appears in their dossier and related workflows.
      </div>

      <div class="tw-flex tw-gap-3 tw-items-end">
        <div class="tw-flex-1" v-if="!showCreateForm">
          <Field>
            <Label>Search people</Label>
            <TextInput
              v-model="searchQuery"
              :disabled="isLinking || isCreating"
              placeholder="Search by name or email"
              @input="handleSearchInput"
            />
          </Field>
        </div>
        <Button :disabled="isLinking || isCreating" @click="toggleCreateForm">
          <span>{{ showCreateForm ? 'Back to search' : 'Create person' }}</span>
        </Button>
      </div>

      <div v-if="errorMessage" class="tw-text-red-600 tw-text-sm">
        {{ errorMessage }}
      </div>

      <div v-if="!showCreateForm">
        <div v-if="isSearching" class="tw-text-gray-500 tw-text-sm">
          Searching…
        </div>
        <div v-else>
          <div v-if="results.length === 0" class="tw-text-gray-500 tw-text-sm">
            No results yet. Try searching by full name or email.
          </div>
          <div class="tw-flex tw-flex-col tw-divide-y tw-border tw-rounded">
            <div
              v-for="person in results"
              class="tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2"
            >
              <div>
                <div class="tw-font-medium">{{ person.name }}</div>
                <div class="tw-text-xs tw-text-gray-500">{{ person.email }}</div>
              </div>
              <Button
                size="3"
                :disabled="isLinking"
                @click="connectToPerson(person)"
              >
                <span v-if="!isLinking">Connect</span>
                <span v-else>
                  <i class="fas fa-spinner fa-spin" />
                  <span class="tw-ml-1">Connecting…</span>
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div v-else>
        <NewPersonModal @onClose="toggleCreateForm" @onAdd="handlePersonAdded" />
      </div>

      <div class="tw-flex tw-justify-end tw-gap-2" v-if="!showCreateForm">
        <Button @click="close" :disabled="isLinking || isCreating">Cancel</Button>
      </div>
    </div>
  </StakeModal>
  
</template>

<script>
import StakeModal from '@stakeui/StakeModal.vue';
import {
  Button,
  Field,
  Label,
  TextInput,
} from '@stakeui';
import NewPersonModal from "@frontend/ui/features/people/NewPersonModal.vue";

export default {
  name: 'MeetingConnectPromptModal',
  components: {
    StakeModal,
    Button,
    Field,
    Label,
    TextInput,
    NewPersonModal,
  },
  props: {
    meetingId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      isOpen: false,
      searchQuery: '',
      searchTimer: null,
      isSearching: false,
      results: [],
      isLinking: false,
      isCreating: false,
      errorMessage: '',
      showCreateForm: false,
      hasMarkedSeen: false,
    };
  },
  watch: {
    // If modal is closed in any way (overlay click, ESC, or programmatically),
    // ensure we mark the prompt as seen once per open lifecycle.
    isOpen(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        this.markSeen();
      }
    },
  },
  computed: {
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
        'Content-Type': 'application/json',
      };
    },
    canCreate() {
      return true;
    },
  },
  methods: {
    open() {
      this.isOpen = true;
      this.errorMessage = '';
      this.results = [];
      this.searchQuery = '';
      this.showCreateForm = false;
      this.hasMarkedSeen = false;
      // Mark as seen as soon as the user is shown the prompt
      this.markSeen();
    },
    close() {
      this.isOpen = false;
      this.$emit('cancel');
      // Best-effort mark seen
      this.markSeen();
    },
    markSeen() {
      if (this.hasMarkedSeen || !this.meetingId) return;
      try {
        fetch(`/api/v1/meetings/${this.meetingId}/connect-prompt-seen/`, {
          method: 'POST',
          headers: this.headers,
        })
          .then(() => {
            this.hasMarkedSeen = true;
          })
          .catch(() => {});
      } catch (e) {
        // no-op
      }
    },
    handleSearchInput() {
      clearTimeout(this.searchTimer);
      if (!this.searchQuery || this.searchQuery.trim().length === 0) {
        this.results = [];
        return;
      }
      this.searchTimer = setTimeout(this.searchEntities, 250);
    },
    async searchEntities() {
      try {
        this.isSearching = true;
        const url = `/api/v1/entity/?entity_types=Prospect,Client&search=${encodeURIComponent(this.searchQuery.trim())}`;
        const res = await fetch(url, { method: 'GET', headers: this.headers });
        if (!res.ok) {
          this.errorMessage = 'Search failed. Please try again.';
          this.results = [];
          return;
        }
        const data = await res.json();
        this.results = Array.isArray(data.entities) ? data.entities : [];
      } catch (e) {
        this.errorMessage = 'Search failed. Please try again.';
        this.results = [];
      } finally {
        this.isSearching = false;
      }
    },
    async connectToPerson(person) {
      if (!person || !person.external_id) return;
      this.isLinking = true;
      this.errorMessage = '';
      try {
        const res = await fetch(`/api/v1/meetings/${this.meetingId}/entity/`, {
          method: 'POST',
          headers: this.headers,
          body: JSON.stringify({ entity_external_id: person.external_id }),
        });
        if (!res.ok) {
          this.errorMessage = 'Failed to connect meeting';
          return;
        }
        this.isOpen = false;
        this.$emit('connected', person);
      } catch (e) {
        this.errorMessage = 'Failed to connect meeting';
      } finally {
        this.isLinking = false;
      }
    },
    toggleCreateForm() {
      this.showCreateForm = !this.showCreateForm;
      this.errorMessage = '';
    },
    async createAndConnect() {},
    async handlePersonAdded(created) {
      if (!created || !created.external_id) return;
      await this.connectToPerson(created);
    },
  },
};
</script>

<style scoped>
.input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.25rem;
}
</style>


